import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Sparkles, Plus, Trash2, Loader2 } from "lucide-react";

/**
 * Admin-only What's New publisher. Drops into any admin tab. Lets founder/
 * admin draft a new release-notes entry — visible the moment they hit
 * Publish, dismissible per-user, persisted in the `whats_new` collection.
 */
const WhatsNewPublisher = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ version: "", title: "", itemsRaw: "", audience: "all" });
  const [saving, setSaving] = useState(false);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/whats-new`);
      setEntries(r.data.entries || []);
    } catch {
      toast.error("Failed to load entries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, []);

  const handlePublish = async () => {
    const items = form.itemsRaw.split("\n").map(s => s.trim()).filter(Boolean);
    if (!form.version.trim() || !form.title.trim() || items.length === 0) {
      toast.error("Version, title, and at least one item required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/whats-new`, {
        version: form.version.trim(),
        title: form.title.trim(),
        items,
        audience: form.audience,
      });
      toast.success("Published — members will see the banner on their next page load");
      setForm({ version: "", title: "", itemsRaw: "", audience: "all" });
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (entryId) => {
    if (!window.confirm("Hide this entry from all users? (Soft-delete — existing dismissals stay recorded.)")) return;
    try {
      await axios.delete(`${API}/admin/whats-new/${entryId}`);
      toast.success("Hidden");
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to hide");
    }
  };

  return (
    <div className="space-y-6" data-testid="whats-new-publisher">
      <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
        <h3 className="text-base font-medium text-[#6B4E8A] flex items-center gap-2 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          <Sparkles className="w-4 h-4 text-[#8DB5A0]" />
          Publish a new "What's New" entry
        </h3>
        <p className="text-xs text-[#64748B] mb-4">
          Shows as a dismissible banner across the top of every authenticated page. Each user dismisses once. Use **bold** for emphasis.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-xs text-[#475569]">Version label</Label>
            <Input
              value={form.version}
              onChange={(e) => setForm({ ...form, version: e.target.value })}
              placeholder="Sprint 2 — 2026-05-20"
              className="mt-1 bg-white border-[#E5DFEC]"
              data-testid="wn-version-input"
            />
          </div>
          <div>
            <Label className="text-xs text-[#475569]">Audience</Label>
            <select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              className="mt-1 w-full h-10 bg-white border border-[#E5DFEC] rounded-md px-3 text-sm"
              data-testid="wn-audience-select"
            >
              <option value="all">Everyone</option>
              <option value="admin">Admin only</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <Label className="text-xs text-[#475569]">Headline</Label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="5 fixes shipped this week"
            className="mt-1 bg-white border-[#E5DFEC]"
            data-testid="wn-title-input"
          />
        </div>
        <div className="mb-4">
          <Label className="text-xs text-[#475569]">Items (one per line · use **bold** for emphasis)</Label>
          <Textarea
            value={form.itemsRaw}
            onChange={(e) => setForm({ ...form, itemsRaw: e.target.value })}
            placeholder="**Resources tab** now expands on click&#10;**Shift Tracker** date alignment fixed"
            className="mt-1 min-h-[120px] bg-white border-[#E5DFEC] font-mono text-sm"
            data-testid="wn-items-input"
          />
        </div>
        <Button
          onClick={handlePublish}
          disabled={saving}
          className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-5"
          data-testid="wn-publish-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" />Publish</>}
        </Button>
      </div>

      <div>
        <h3 className="text-sm font-medium text-[#6B4E8A] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
          Recent entries
        </h3>
        {loading ? (
          <p className="text-sm text-[#94A3B8]">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">No entries yet.</p>
        ) : (
          <ul className="space-y-2">
            {entries.map(e => (
              <li
                key={e.entry_id}
                className={`p-4 rounded-xl border ${e.is_active ? "bg-white border-[#E5DFEC]" : "bg-[#FEF2F2] border-[#FECACA]"}`}
                data-testid={`wn-entry-${e.entry_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest text-[#94A3B8]">{e.version}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${e.audience === "admin" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#F0ECF7] text-[#6B4E8A]"}`}>
                        {e.audience}
                      </span>
                      {!e.is_active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#DC2626]">hidden</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-[#6B4E8A] mt-1">{e.title}</p>
                    <ul className="text-xs text-[#475569] mt-1.5 space-y-0.5">
                      {(e.items || []).map((it, i) => (
                        <li key={`${e.entry_id}-${i}`}>· {it}</li>
                      ))}
                    </ul>
                  </div>
                  {e.is_active && (
                    <button
                      type="button"
                      onClick={() => handleDeactivate(e.entry_id)}
                      className="p-1.5 rounded-lg text-[#EF4444] hover:bg-[#FEE2E2] transition-colors shrink-0"
                      title="Hide entry"
                      data-testid={`wn-hide-${e.entry_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WhatsNewPublisher;
